import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Col, Row, Badge, Button } from "react-bootstrap";
import { 
    Shield,
    FileText, 
    CreditCard, 
    Users, 
    Activity,
    Calendar,
    Clock,
    Lock,
    Unlock,
    Settings,
    ChevronRight,
    BarChart2,
    User,
    Truck
} from "react-feather";
import { ModalContext } from "context/ModalContext";
import UnlockFeatures from 'components/widgets/modals/UnlockFeatures';

const UnderwritingServices = () => {
    const history = useHistory();
    const { setModalShow, modalShow, modalClose } = useContext(ModalContext);
    
    // Selected service for modal
    const [selectedService, setSelectedService] = useState(null);
    
    // Track service statuses and expiry dates
    const [services, setServices] = useState([
        {
            id: 'underwriters',
            title: 'Underwriters Management',
            description: 'Manage underwriter profiles, assignments, and performance tracking',
            icon: Users,
            color: '#1890ff',
            price: 75,
            status: 'active',
            expiryDate: '2025-03-15',
            remainingDays: 45,
            route: '/underwriting/underwriters'
        },
        {
            id: 'products',
            title: 'Insurance Products',
            description: 'Manage insurance product configurations, pricing, and terms',
            icon: Shield,
            color: '#52c41a',
            price: 100,
            status: 'locked',
            expiryDate: null,
            remainingDays: 0,
            route: '/underwriting/products'
        },
        {
            id: 'claims',
            title: 'Claims Processing',
            description: 'Advanced claims assessment, processing, and settlement tools',
            icon: FileText,
            color: '#faad14',
            price: 120,
            status: 'active',
            expiryDate: '2025-02-20',
            remainingDays: 12,
            route: '/underwriting/claims'
        },
        {
            id: 'risk-assessment',
            title: 'Risk Assessment',
            description: 'Comprehensive risk evaluation and scoring algorithms',
            icon: BarChart2,
            color: '#722ed1',
            price: 90,
            status: 'locked',
            expiryDate: null,
            remainingDays: 0,
            route: '/underwriting/risk-assessment'
        },
        {
            id: 'policy-management',
            title: 'Policy Management',
            description: 'Policy lifecycle management from issuance to renewal',
            icon: CreditCard,
            color: '#fa541c',
            price: 80,
            status: 'active',
            expiryDate: '2025-04-10',
            remainingDays: 72,
            route: '/underwriting/policy-management'
        },
        {
            id: 'reinsurance',
            title: 'Reinsurance Module',
            description: 'Reinsurance placement, tracking, and settlement management',
            icon: Activity,
            color: '#13c2c2',
            price: 150,
            status: 'locked',
            expiryDate: null,
            remainingDays: 0,
            route: '/underwriting/reinsurance'
        },
        {
            id: 'motor-valuation',
            title: 'Motor Valuation',
            description: 'Automated motor vehicle valuation and assessment tools',
            icon: Truck,
            color: '#eb2f96',
            price: 60,
            status: 'active',
            expiryDate: '2025-03-25',
            remainingDays: 55,
            route: '/underwriting/motor-valuation'
        },
        {
            id: 'compliance-audit',
            title: 'Compliance & Audit',
            description: 'Regulatory compliance monitoring and audit trail management',
            icon: Settings,
            color: '#f5222d',
            price: 110,
            status: 'locked',
            expiryDate: null,
            remainingDays: 0,
            route: '/underwriting/compliance-audit'
        },
        {
            id: 'analytics-reporting',
            title: 'Analytics & Reporting',
            description: 'Advanced analytics, KPI tracking, and business intelligence',
            icon: Activity,
            color: '#52c41a',
            price: 95,
            status: 'active',
            expiryDate: '2025-05-01',
            remainingDays: 85,
            route: '/underwriting/analytics-reporting'
        }
    ]);

    // Free routes available to all users
    const freeRoutes = [
        {
            id: 'reports',
            title: 'Reports',
            description: 'View and generate underwriting reports',
            icon: FileText,
            route: '/underwriting/reports'
        },
        {
            id: 'my-profession',
            title: 'My Profession',
            description: 'Professional profile and certifications',
            icon: User,
            route: '/underwriting/my-profession'
        },
        {
            id: 'dmvic-cards',
            title: 'DMVIC Cards',
            description: 'Digital Motor Vehicle Insurance Cards',
            icon: CreditCard,
            route: '/underwriting/dmvic-cards'
        }
    ];

    const handleUnlockService = (service) => {
        try {
            setSelectedService(service);
            setModalShow(true);
        } catch (error) {
            console.error('Error opening unlock modal:', error);
        }
    };

    const handleServiceUnlocked = () => {
        try {
            // Update the service status after successful unlock
            if (selectedService) {
                setServices(prev => prev.map(service => 
                    service.id === selectedService.id 
                        ? { 
                            ...service, 
                            status: 'active',
                            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
                            remainingDays: 30
                        }
                        : service
                ));
            }
            
            // Close modal and reset selected service
            modalClose();
            setSelectedService(null);
            
            console.log(`Service unlocked successfully: ${selectedService?.title}`);
        } catch (error) {
            console.error('Error handling service unlock:', error);
        }
    };

    const handleConfigureService = (serviceId) => {
        try {
            console.log(`Configuring service: ${serviceId}`);
            // Navigate to service configuration page
            history.push(`/underwriting/configure/${serviceId}`);
        } catch (error) {
            console.error('Error configuring service:', error);
        }
    };

    const handleNavigateToService = (service) => {
        try {
            if (service.status === 'active') {
                console.log(`Navigating to ${service.title}: ${service.route}`);
                history.push(service.route);
            } else {
                console.log(`Service ${service.title} is locked`);
            }
        } catch (error) {
            console.error('Error navigating to service:', error);
        }
    };

    const handleNavigateToFreeRoute = (route) => {
        try {
            console.log(`Navigating to free route: ${route.route}`);
            history.push(route.route);
        } catch (error) {
            console.error('Error navigating to free route:', error);
        }
    };

    const formatExpiryDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getRemainingDaysColor = (days) => {
        if (days > 30) return '#22c55e';
        if (days > 7) return '#f59e0b';
        return '#ef4444';
    };

    return (
        <div className="card p-4" style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {/* Header Section */}
            <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                        <Shield size={32} className="text-primary mr-3" />
                        <h2 className="mb-0" style={{ fontWeight: '600', color: '#1f2937' }}>
                            Underwriting Module Services
                        </h2>
                    </div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => history.push('/underwriting/configure-permissions')}
                        style={{ 
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Settings size={14} className="mr-2" />
                        Configure Permissions
                    </Button>
                </div>
                <p style={{ fontSize: '1rem', color: '#6b7280', margin: 0 }}>
                    Access professional underwriting tools and services to enhance your insurance assessments
                </p>
            </div>

            {/* Unlock Service Modal */}
            <UnlockFeatures 
                show={modalShow} 
                onHide={modalClose} 
                service={selectedService}
                onServiceUnlocked={handleServiceUnlocked}
            />

            {/* Premium Services Section */}
            <div className="mb-5">
                <h4 className="mb-4" style={{ fontWeight: '600', color: '#1f2937' }}>Premium Services</h4>
                
                <Row>
                    {services.map((service) => {
                        const IconComponent = service.icon;
                        const isActive = service.status === 'active';
                        const isLocked = service.status === 'locked';
                        
                        return (
                            <Col md={4} key={service.id} className="mb-4">
                                <Card 
                                    className="h-100 shadow-sm" 
                                    style={{ 
                                        borderRadius: "12px", 
                                        border: "1px solid #e5e7eb",
                                        transition: "all 0.3s ease",
                                        position: "relative",
                                        cursor: isActive ? "pointer" : "default",
                                        opacity: isLocked ? 0.8 : 1
                                    }}
                                    onClick={() => isActive && handleNavigateToService(service)}
                                    onMouseEnter={(e) => {
                                        if (isActive) {
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                            e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (isActive) {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                        }
                                    }}
                                >
                                    {/* Status Indicator */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "12px",
                                            right: "12px",
                                            background: isActive ? "#22c55e" : "#ef4444",
                                            borderRadius: "50%",
                                            width: "24px",
                                            height: "24px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#ffffff",
                                            zIndex: 10
                                        }}
                                    >
                                        {isActive ? <Unlock size={12} /> : <Lock size={12} />}
                                    </div>

                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div 
                                                style={{
                                                    width: "48px",
                                                    height: "48px",
                                                    borderRadius: "12px",
                                                    background: `${service.color}15`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                <IconComponent 
                                                    size={24} 
                                                    style={{ color: service.color }} 
                                                />
                                            </div>
                                        </div>
                                        
                                        <Card.Title 
                                            style={{ 
                                                fontSize: "1.1rem", 
                                                fontWeight: "600",
                                                color: "#1f2937",
                                                marginBottom: "8px"
                                            }}
                                        >
                                            {service.title}
                                        </Card.Title>
                                        
                                        <Card.Text 
                                            style={{ 
                                                fontSize: "0.85rem", 
                                                color: "#6b7280",
                                                lineHeight: "1.4",
                                                marginBottom: "16px",
                                                height: "40px"
                                            }}
                                        >
                                            {service.description}
                                        </Card.Text>

                                        {/* Price Display */}
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Price:</span>
                                                <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                                                    {service.price} KSH
                                                </span>
                                            </div>
                                        </div>

                                        {/* Status-specific content */}
                                        {isActive && (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Expires:</span>
                                                    <span style={{ fontSize: '0.85rem', color: '#374151' }}>
                                                        {formatExpiryDate(service.expiryDate)}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Remaining:</span>
                                                    <Badge 
                                                        style={{ 
                                                            background: getRemainingDaysColor(service.remainingDays),
                                                            color: 'white',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        {service.remainingDays} days
                                                    </Badge>
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        {isLocked && (
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="flex-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnlockService(service);
                                                    }}
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    <Unlock size={14} className="mr-1" />
                                                    Unlock
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="flex-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleConfigureService(service.id);
                                                    }}
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    <Settings size={14} className="mr-1" />
                                                    Configure
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </div>

            {/* Free Routes Section */}
            <div className="mb-4">
                <h4 className="mb-4" style={{ fontWeight: '600', color: '#1f2937' }}>Available Routes</h4>
                
                <Row>
                    {freeRoutes.map((route) => {
                        const IconComponent = route.icon;
                        
                        return (
                            <Col md={4} key={route.id} className="mb-3">
                                <Card 
                                    className="shadow-sm" 
                                    style={{ 
                                        borderRadius: "8px", 
                                        border: "1px solid #e5e7eb",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease"
                                    }}
                                    onClick={() => handleNavigateToFreeRoute(route)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.borderColor = "#3b82f6";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.borderColor = "#e5e7eb";
                                    }}
                                >
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <div 
                                                    style={{
                                                        width: "32px",
                                                        height: "32px",
                                                        borderRadius: "8px",
                                                        background: "#f3f4f6",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        marginRight: "12px"
                                                    }}
                                                >
                                                    <IconComponent 
                                                        size={16} 
                                                        style={{ color: "#6b7280" }} 
                                                    />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0" style={{ fontWeight: '500', color: '#1f2937', fontSize: '0.95rem' }}>
                                                        {route.title}
                                                    </h6>
                                                    <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                                                        {route.description}
                                                    </small>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} style={{ color: '#9ca3af' }} />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </div>
        </div>
    );
};

export default UnderwritingServices;